import { Drawer, Empty, Skeleton, Tag, Typography, theme as antdTheme } from 'antd';
import {
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { auditApi, type EntityRevision, type FieldChange } from '@/api/audit';
import { useAuthStore } from '@/store/auth';

interface Props {
  /** Envers entity name — matches what {@code /audit/history/types} returns. */
  entityType?: string | null;
  /** Row PK whose history we want. */
  entityId?: number | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Right-side drawer rendering an entity's Envers history. Reusable from any
 * detail page — pass {@code entityType} ("Student", "FeeReceipt", "Staff",
 * etc.) and the row id, flip {@code open} to true.
 *
 * Backend wiring: GET /audit/history?type=&id= returns a {@link EntityHistoryResponse}
 * with revisions newest-first; each carries a {@code revisionType}
 * (ADD / MOD / DEL), the actor's user id + username, and a list of
 * field-level changes (oldValue → newValue) when known.
 *
 * Visibility is gated by the same `ROLE_SUPER_ADMIN` / `ROLE_PRINCIPAL` check
 * the dedicated /audit/history page uses — the drawer renders an "Insufficient
 * permission" placeholder for everyone else so casual users on a detail page
 * don't get a 403 toast.
 */
export function AuditHistoryDrawer({ entityType, entityId, open, onClose }: Props) {
  const { token } = antdTheme.useToken();
  const canView = useAuthStore(
    (s) => s.hasAuthority('ROLE_SUPER_ADMIN') || s.hasAuthority('ROLE_PRINCIPAL'),
  );

  const query = useQuery({
    queryKey: ['audit', 'history', entityType, entityId],
    queryFn: () => auditApi.history(entityType!, entityId!),
    enabled: open && canView && entityType != null && entityId != null,
    staleTime: 30 * 1000,
  });

  return (
    <Drawer
      title={
        entityType && entityId
          ? `History · ${entityType} #${entityId}`
          : 'History'
      }
      open={open}
      onClose={onClose}
      size={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 16 : 560)}
      destroyOnHidden
    >
      {!canView ? (
        <Empty
          description={
            <Typography.Text type="secondary">
              You don't have permission to view record history.
            </Typography.Text>
          }
        />
      ) : query.isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : query.isError ? (
        <Empty
          description={
            <Typography.Text type="secondary">
              Couldn't load history. The record may not be audited yet, or the
              backend is unreachable.
            </Typography.Text>
          }
        />
      ) : query.data == null || query.data.revisions.length === 0 ? (
        <Empty description="No changes recorded for this record." />
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {query.data.revisions.map((r) => (
            <RevisionCard key={r.revision} rev={r} token={token} />
          ))}
        </div>
      )}
    </Drawer>
  );
}

interface RevisionCardProps {
  rev: EntityRevision;
  token: ReturnType<typeof antdTheme.useToken>['token'];
}

function RevisionCard({ rev, token }: RevisionCardProps) {
  const meta = REV_META[rev.revisionType];
  return (
    <div
      style={{
        position: 'relative',
        padding: '12px 14px 12px 38px',
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 10,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 12,
          top: 12,
          fontSize: 16,
          color: meta.color,
        }}
      >
        {meta.icon}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Tag color={meta.tagColor}>{meta.label}</Tag>
        <Typography.Text strong>Rev #{rev.revision}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(rev.revisionTimestamp).format('D MMM YYYY · HH:mm')}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          by {rev.updatedByUsername ?? (rev.updatedBy != null ? `user #${rev.updatedBy}` : 'system')}
        </Typography.Text>
      </div>

      {rev.changes.length > 0 && (
        <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
          {rev.changes.map((c) => (
            <ChangeRow key={c.field} change={c} token={token} />
          ))}
        </div>
      )}

      {rev.changes.length === 0 && rev.revisionType !== 'ADD' && (
        <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
          (No tracked field changes on this revision.)
        </Typography.Text>
      )}
    </div>
  );
}

function ChangeRow({
  change,
  token,
}: {
  change: FieldChange;
  token: ReturnType<typeof antdTheme.useToken>['token'];
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: 12 }}>
      <Typography.Text type="secondary" style={{ overflowWrap: 'anywhere' }}>
        {change.field}
      </Typography.Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <ValuePill value={change.oldValue} dim color={token.colorTextTertiary} bg={token.colorFillQuaternary} />
        <span style={{ color: token.colorTextTertiary }}>→</span>
        <ValuePill value={change.newValue} color={token.colorText} bg={token.colorPrimaryBg} />
      </div>
    </div>
  );
}

function ValuePill({
  value, dim, color, bg,
}: { value: unknown; dim?: boolean; color: string; bg: string }) {
  const text = formatValue(value);
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 4,
        padding: '1px 6px',
        fontFamily: text === '∅' ? 'inherit' : 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        textDecoration: dim ? 'line-through' : undefined,
        opacity: dim ? 0.7 : 1,
        maxWidth: 220,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={text === '∅' ? 'empty' : text}
    >
      {text}
    </span>
  );
}

function formatValue(v: unknown): string {
  if (v == null) return '∅';
  if (typeof v === 'string') return v.length > 0 ? v : '∅';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const REV_META: Record<
  EntityRevision['revisionType'],
  { label: string; icon: React.ReactNode; color: string; tagColor: string }
> = {
  ADD: {
    label: 'Created',
    icon: <PlusCircleOutlined />,
    color: 'var(--ant-color-success)',
    tagColor: 'success',
  },
  MOD: {
    label: 'Updated',
    icon: <EditOutlined />,
    color: 'var(--ant-color-info)',
    tagColor: 'processing',
  },
  DEL: {
    label: 'Deleted',
    icon: <MinusCircleOutlined />,
    color: 'var(--ant-color-error)',
    tagColor: 'error',
  },
};
